// TODO add hook to reuse hoverID setting and cbs for mouse events

import * as THREE from "three";
import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import { a } from "@react-spring/three";
import { useFrame } from "react-three-fiber";
import { Tooltip } from "./Tooltip";

import {
  useTransitionAttributeArray,
  useMemoAttributeArrays,
} from "../../utils";
import { InstancedPhongMaterial } from "../materials/custom";

// const AnimatedInstancedPhongMaterial = animated(InstancedPhongMaterial);

// TODO animate opacity when opacityfunc changes
export default function Lines({
  data,
  scaleX,
  x = (d) => 1,
  y = (d) => 1,
  z = (d) => 1,
  selected = [],
  onHover = () => {},
  opacityFunc = (d) => d.opacity,
  colorFunc = (d) => 0xffffff,
  width = 0.03,
  height = 0.2,
  depth = 0.03,
  name = "",
  material = {},
  renderFuzziness = false,
  rotation = [0, 0, 0],
  showTooltip = false,
  transitionConfig = {},
}) {
  const [hoveredId, setHoveredId] = useState();
  const previousId = useRef();

  const tempObject = useMemo(() => new THREE.Object3D(), []);
  const tempColor = useMemo(() => new THREE.Color(), []);
  const MAX_DRAW_INSTANCES = 10000;
  const userDataArray = useMemo(() => data.map((d) => ({ ...d })), [data]);
  const mesh = useRef();

  const {
    colorArray,
    opacityArray,
    idArray,
    animationDelayArray,
    renderFuzzinessArray,
  } = useMemoAttributeArrays({
    colorFunc,
    data,
    opacityFunc,
  });

  useTransitionAttributeArray({
    data,
    selected,
    opacityFunc,
    meshRef: mesh,
    ...transitionConfig,
  });

  // TODO this is similar code with Spheres.js
  const particles = useMemo(() => {
    const temp = [];
    data.forEach((d, i) => {
      const size = 0.1;

      const sx = scaleX(d);
      let w;
      if (renderFuzziness) {
        w = d.width;
      } else {
        w = width * sx;
      }
      const h = height;

      temp.push({
        size,
        color: colorFunc(d),
        opacity: opacityFunc(d),
        scaleX: sx,
        width: w,
        height: h,
        depth,
        x: x(d),
        y: y(d),
        z: z(d),
      });
    });
    return temp;
  }, [
    data,
    colorFunc,
    opacityFunc,
    scaleX,
    width,
    height,
    depth,
    renderFuzziness,
    x,
    y,
    z,
  ]);

  // MOUSE HOVER HANDLERS ========================

  useEffect(() => {
    if (!mesh.current) return;

    if (previousId.current !== hoveredId) {
      if (!isNaN(hoveredId)) {
        // don't send onHover callback if element is hidden
        mesh.current.geometry.attributes.tOpacity.array[hoveredId] > 0 &&
          onHover([userDataArray[hoveredId]]);
      } else {
        onHover([]);
      }

      // set currently hovered particle
      if (hoveredId) {
        tempColor.set(0xffffff).toArray(colorArray, hoveredId * 3);
      }

      // reset previously hovered particle
      if (previousId.current) {
        tempColor
          .set(particles[previousId.current].color)
          .toArray(colorArray, previousId.current * 3);
      }

      mesh.current.geometry.attributes.color.needsUpdate = true;
      mesh.current.instanceMatrix.needsUpdate = true;
    }
    previousId.current = hoveredId;
  }, [
    mesh,
    hoveredId,
    onHover,
    userDataArray,
    colorArray,
    depth,
    height,
    width,
    particles,
    tempColor,
    tempObject,
  ]);

  const onPointerMoveCallback = useCallback((e) => {
    e.stopPropagation();
    if (mesh.current.geometry.attributes.tOpacity.array[e.instanceId] > 0) {
      e.target.setPointerCapture(e.pointerId);
      setHoveredId(e.instanceId);
    }
  }, []);
  const onPointerOutCallback = useCallback((e) => {
    e.stopPropagation();
    setHoveredId(undefined);
  }, []);
  // END MOUSE HOVER HANDLERS ========================

  useEffect(() => {
    if (!mesh.current) return;
    particles.forEach((particle, i) => {
      let { x, y, z, width, height, depth } = particle;
      tempObject.position.set(x, y, z);
      tempObject.rotation.set(...rotation);
      const scale = 1;
      tempObject.scale.set(scale * width, scale * height, scale * depth);
      tempObject.updateMatrix();
      mesh.current.setMatrixAt(i, tempObject.matrix);
    });
    mesh.current.count = data.length;
    mesh.current.instanceMatrix.needsUpdate = true;
  }, [data, rotation, particles, depth, height, width, tempObject]);

  useFrame((state) => {
    if (!mesh.current) return;
    const { clock } = state;
    mesh.current.material.clock = clock.elapsedTime;
  });

  return (
    <>
      {showTooltip && (
        <Tooltip data={!isNaN(hoveredId) ? userDataArray[hoveredId] : null} />
      )}
      <a.instancedMesh
        ref={mesh}
        name={name}
        args={[null, null, MAX_DRAW_INSTANCES]}
        castShadow={true}
        receiveShadow={true}
        onPointerMove={onPointerMoveCallback}
        onPointerOut={onPointerOutCallback}
        // onPointerDown={onClick}
        // onPointerMissed={onClick}
        userData={userDataArray}
      >
        {/* Try also <planeBufferGeometry attach="geometry" args={[1, 1, 5, 5]}> */}
        <boxBufferGeometry attach="geometry" args={[1, 1, 1]}>
          <instancedBufferAttribute
            attachObject={["attributes", "color"]}
            args={[colorArray, 3]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "instanceOpacity"]}
            args={[opacityArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "tOpacity"]}
            args={[opacityArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "animationDelay"]}
            args={[animationDelayArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "renderFuzziness"]}
            args={[renderFuzzinessArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "instanceId"]}
            args={[idArray, 1]}
          />
        </boxBufferGeometry>
        <InstancedPhongMaterial
          attach="material"
          transparent={true}
          depthTest={true}
          side={THREE.DoubleSide}
          blending={THREE.NormalBlending}
          // animationDelta={1}
          // animationStart={0}
          // opacity={op}
          // dithering={true}
          metalness={0}
          emissive={0x0}
          shininess={0}
          specular={0xff0000}
          defines={{
            USE_UV: renderFuzziness,
            USE_DATE_FUZZINESS: renderFuzziness,
          }}
          // color="white"
          vertexColors={THREE.VertexColors}
          {...material}
        />
      </a.instancedMesh>
    </>
  );
}
